/**
 * queueCounts — the one number the consultant's Queue shows.
 *
 * The product audit (2026-10-28, Consultant walk finding C7) found the
 * dashboard answering "what do I do next" in five places with five counts
 * (an answers panel, a Review Queue badge, Action Items, Needs Your Action,
 * Recommended Next Steps). Ledger L22 asks for one queue with one count.
 * These helpers are pure so the count and its wording can be tested.
 */

import { normalizeStatus } from './configItemLifecycle';

export type QueueSectionId = 'answers' | 'submissions' | 'review' | 'actions' | 'decisions' | 'escalations' | 'todos';

export interface QueueSection {
  id: QueueSectionId;
  label: string;
  count: number;
}

export const QUEUE_SECTION_LABELS: Record<QueueSectionId, string> = {
  answers: 'Answers to check',
  submissions: 'Client submissions',
  review: 'For your review',
  actions: 'Action items',
  decisions: 'Decisions',
  escalations: 'Escalations',
  todos: 'To-dos',
};

/** How a section reads in the breakdown line, for one and for many. */
const SECTION_WORDS: Record<QueueSectionId, { one: string; many: string }> = {
  answers: { one: 'answer to check', many: 'answers to check' },
  submissions: { one: 'client submission', many: 'client submissions' },
  review: { one: 'for your review', many: 'for your review' },
  actions: { one: 'action item', many: 'action items' },
  decisions: { one: 'decision', many: 'decisions' },
  escalations: { one: 'escalation', many: 'escalations' },
  todos: { one: 'to-do', many: 'to-dos' },
};

/** "174 answers to check · 69 action items · 1 to-do", non-empty sections only; '' when nothing. */
export function queueBreakdown(sections: QueueSection[]): string {
  return sections
    .filter((s) => s.count > 0)
    .map((s) => `${s.count} ${s.count === 1 ? SECTION_WORDS[s.id].one : SECTION_WORDS[s.id].many}`)
    .join(' · ');
}

/** The headline while sources are still loading, so the number never jumps under the reader. */
export const QUEUE_COUNTING = 'Counting what needs you…';

/** Sections in the order the Queue shows them, from the counts per source. */
export function queueSections(counts: Partial<Record<QueueSectionId, number>>): QueueSection[] {
  return (Object.keys(QUEUE_SECTION_LABELS) as QueueSectionId[]).map((id) => ({
    id,
    label: QUEUE_SECTION_LABELS[id],
    count: Math.max(0, Math.floor(Number(counts[id] ?? 0) || 0)),
  }));
}

/** The one count: every section added up. */
export function queueTotal(sections: QueueSection[]): number {
  return sections.reduce((sum, s) => sum + s.count, 0);
}

/** The headline above the Queue. */
export function queueHeadline(total: number): string {
  if (total <= 0) return 'Nothing needs you right now';
  if (total === 1) return '1 thing needs you';
  return `${total} things need you`;
}

/**
 * Whether a review-queue item still needs the consultant: it is in discovery
 * (the server already filtered to the consultant's court). Locked, built and
 * blocked items are the Deliver tab's. The Queue's count and the review
 * panel's default filter both use this, so the headline never counts what
 * the panel hides.
 */
export function reviewItemNeedsAttention(item: { status?: string | null; implementationStatus?: unknown } | null | undefined): boolean {
  return normalizeStatus(item ?? undefined) === 'discovery';
}

/**
 * The one line that replaced the three stat cards on the consultant landing:
 * clients, what needs you (the Queue's count), clients with blockers.
 */
export function workSummaryLabels(counts: { clients: number; needsYou: number; blocked: number }): { clients: string; needsYou: string; blocked: string } {
  const c = Math.max(0, counts.clients | 0), n = Math.max(0, counts.needsYou | 0), b = Math.max(0, counts.blocked | 0);
  return {
    clients: `${c} active ${c === 1 ? 'client' : 'clients'}`,
    needsYou: n === 0 ? 'nothing needs you' : `${n} need${n === 1 ? 's' : ''} you`,
    blocked: b === 0 ? 'no blockers' : `${b} ${b === 1 ? 'client' : 'clients'} with blockers`,
  };
}
